import { useCallback, useEffect, useMemo, useState } from 'react';
import { CRMModel } from '../models/CRM';
import { useCRM } from './useCRM';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import type { Filters } from '../models/Filters';
import { useCRMFilters } from './useCRMFilters';

type SortingState = { field: string; direction: 'asc' | 'desc' } | null;

const INITIAL_SELECTED_COLUMNS = [
  'ref',
  'status',
  'client_name',
  'shipping_country_code',
  'created_at',
  'total',
  'tags',
  'notes',
  'actions',
] as const;

const FIXED_COLUMNS = ['ref', 'status'] as const;

// Campos permitidos por la API para ordenación
const ALLOWED_SORT_FIELDS = ['ref', 'created_at', 'total'] as const;
const DEFAULT_SORT_FIELD = 'created_at';

const FIELD_TO_API_SORT: Record<string, string> = {
  ref: 'ref',
  created_at: 'created_at',
  total: 'total',
};

export const useCRMMain = () => {
  // Filtros delegados a useCRMFilters
  const {
    searchTerm,
    teamIds,
    phoneSearchTerm,
    statusId,
    shopIds,
    tagId,
    shippingCountryCodes,
    paymentMethodIds,
    productIds,
    dateFrom,
    dateTo,
    filterParams,
    hasActiveFilters,
    handleSearch,
    handleTeamIdsChange,
    handlePhoneSearchChange,
    handleStatusChange,
    handleShopIdsChange,
    handleTagChange,
    handleShippingCountryCodesChange,
    handlePaymentMethodChange,
    handleProductIdsChange,
    handleDateRangeChange,
    handleClearFilters,
  } = useCRMFilters();

  // Solo parámetros de ordenación desde la URL
  const { params: sortParams, updateParams: updateSortParams } = useQueryParams({
    sortBy: { type: 'string', default: 'created_at' as string },
    sortDirection: { type: 'string', default: 'desc' as string },
  });

  const [sorting, setSorting] = useState<SortingState>(() => {
    const sortField = sortParams.sortBy || DEFAULT_SORT_FIELD;
    const validField = ALLOWED_SORT_FIELDS.includes(sortField as any) ? sortField : DEFAULT_SORT_FIELD;
    const direction = sortParams.sortDirection === 'asc' ? 'asc' : 'desc';
    return { field: validField, direction };
  });

  const [selectedColumns, setSelectedColumns] = useState<string[]>([...INITIAL_SELECTED_COLUMNS]);
  const [cachedOrders, setCachedOrders] = useState<CRMModel[]>([]);
  const [cachedTotal, setCachedTotal] = useState(0);
  const [selectedOrders, setSelectedOrders] = useState<CRMModel[]>([]);

  // Sincronizar sorting con la URL
  useEffect(() => {
    const sortField = sortParams.sortBy || DEFAULT_SORT_FIELD;
    const validField = ALLOWED_SORT_FIELDS.includes(sortField as any) ? sortField : DEFAULT_SORT_FIELD;
    const direction = sortParams.sortDirection === 'asc' ? 'asc' : 'desc';

    setSorting((prev) => {
      if (prev && prev.field === validField && prev.direction === direction) {
        return prev;
      }
      return { field: validField, direction };
    });
  }, [sortParams.sortBy, sortParams.sortDirection]);

  // Parámetros de ordenación para la API
  const processedFilters = useMemo(() => {
    const sortField = sorting?.field || DEFAULT_SORT_FIELD;
    const validField = ALLOWED_SORT_FIELDS.includes(sortField as any) ? sortField : DEFAULT_SORT_FIELD;
    const apiField = FIELD_TO_API_SORT[validField] || DEFAULT_SORT_FIELD;
    const validDirection: 'asc' | 'desc' = sorting?.direction === 'asc' ? 'asc' : 'desc';

    return {
      sort_by: apiField,
      sort_order: validDirection,
    };
  }, [sorting]);

  // Combinar filtros + ordenación para la API
  const queryFilters = useMemo((): Filters => {
    return {
      ...filterParams,
      ...processedFilters,
    };
  }, [filterParams, processedFilters]);

  // Filtros sin el estado CRM para obtener el total global (el que muestra TotalResults).
  // Si no se hiciera esto, al seleccionar un tab el total cambia al conteo de ese tab.
  const allQueryFilters = useMemo((): Filters => {
    const f = { ...queryFilters } as Record<string, unknown>;
    delete f.crm_status_id;
    delete f.without_crm_status;
    return f as Filters;
  }, [queryFilters]);

  const {
    orders: fetchedOrders,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total: fetchedTotal,
  } = useCRM(queryFilters);

  const { total: allFetchedTotal } = useCRM(allQueryFilters);

  useEffect(() => {
    if (fetchedOrders.length > 0) {
      setCachedOrders(fetchedOrders);
      setCachedTotal(fetchedTotal);
    }
  }, [fetchedOrders.length, fetchedTotal]);

  const handleSelectionChanged = useCallback((rows: CRMModel[]) => {
    setSelectedOrders(rows);
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedOrders([]);
  }, []);

  const handleColumnsChange = useCallback((columns: string[]) => {
    setSelectedColumns(columns);
  }, []);

  const handleSortingChange = useCallback(
    (newSorting: SortingState) => {
      if (newSorting && newSorting.field) {
        const validField = ALLOWED_SORT_FIELDS.includes(newSorting.field as any)
          ? newSorting.field
          : DEFAULT_SORT_FIELD;
        const validDirection = newSorting.direction === 'asc' ? 'asc' : 'desc';

        const validatedSorting: SortingState = {
          field: validField,
          direction: validDirection,
        };

        setSorting(validatedSorting);
        updateSortParams({ sortBy: validField, sortDirection: validDirection });
      } else {
        const defaultSorting: SortingState = {
          field: DEFAULT_SORT_FIELD,
          direction: 'desc',
        };
        setSorting(defaultSorting);
        updateSortParams({ sortBy: DEFAULT_SORT_FIELD, sortDirection: 'desc' });
      }
    },
    [updateSortParams],
  );

  const shouldUseCachedData = !hasActiveFilters && fetchedOrders.length === 0 && cachedTotal > 0;

  const orders = shouldUseCachedData ? cachedOrders : fetchedOrders;
  const total = shouldUseCachedData ? cachedTotal : fetchedTotal;

  const ordersViewProps = useMemo(
    () => ({
      orders,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
    }),
    [orders, fetchNextPage, hasNextPage, isFetchingNextPage, total],
  );

  return {
    orders,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total,
    allTotal: allFetchedTotal,
    sorting,
    selectedColumns,
    selectedOrders,
    searchTerm,
    teamIds,
    phoneSearchTerm,
    statusId,
    shopIds,
    tagId,
    shippingCountryCodes,
    paymentMethodIds,
    productIds,
    dateFrom,
    dateTo,
    filters: queryFilters,
    ordersViewProps,
    hasActiveFilters,
    fixedColumns: [...FIXED_COLUMNS],
    handleSearch,
    handleTeamIdsChange,
    handlePhoneSearchChange,
    handleStatusChange,
    handleShopIdsChange,
    handleTagChange,
    handleShippingCountryCodesChange,
    handlePaymentMethodChange,
    handleProductIdsChange,
    handleDateRangeChange,
    handleColumnsChange,
    handleSortingChange,
    handleClearFilters,
    handleSelectionChanged,
    handleClearSelection,
  };
};
